import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import { Link } from "react-router-dom";
import TaskSkeleton from "./TaskSkeleton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical } from "@fortawesome/free-solid-svg-icons";

export default function Jc_time() {
  const { http, user } = AuthUser();
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const renderProgressBar = (percent, isLeave) => {
    if (isLeave) {
      return (
        <div className="progress">
          <div
            className="progress-bar bg-danger"
            role="progressbar"
            style={{ width: "100%" }}
            aria-valuenow={100}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            leave
          </div>
        </div>
      );
    }

    return (
      <div className="progress">
        <div
          className="progress-bar"
          role="progressbar"
          style={{ width: `${percent}%` }}
          aria-valuenow={percent}
          aria-valuemin="0"
          aria-valuemax="100"
        >
          {percent}%
        </div>
      </div>
    );
  };

  useEffect(() => {
    fetchTaskDetails();
  }, []);

  const fetchTaskDetails = () => {
    setIsLoading(true);
    http
      .get(`/cust_available_total?login_user=${user.id}`)
      .then((res) => {
        setIsLoading(false);
        if (Array.isArray(res.data.data)) {
          setTasks(res.data.data);
        } else {
          console.error("API response is not an array");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Error fetching tasks:", error);
      });
  };

  const saveOrder = async (orderedTasks) => {
    setIsSavingOrder(true);
    const orders = orderedTasks.map((task, index) => ({
      id: task.id,
      jc_sort_order: index + 1,
    }));

    try {
      await http.post("/update_jc_sort_order", {
        login_user: user.id,
        orders,
      });
      toast.success("Order saved");
    } catch (error) {
      console.error("Error saving order:", error);
      toast.error("Failed to save order");
      fetchTaskDetails();
    } finally {
      setIsSavingOrder(false);
    }
  };

  const handleDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const fromIndex = dragIndex !== null
      ? dragIndex
      : Number(e.dataTransfer.getData("text/plain"));

    if (Number.isNaN(fromIndex) || fromIndex === dropIndex) {
      setDragIndex(null);
      return;
    }

    const updated = [...tasks];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(dropIndex, 0, moved);
    setTasks(updated);
    setDragIndex(null);
    saveOrder(updated);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
  };

  const moveRow = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= tasks.length) return;
    const updated = [...tasks];
    const [moved] = updated.splice(index, 1);
    updated.splice(newIndex, 0, moved);
    setTasks(updated);
    saveOrder(updated);
  };

  return (
    <>
      <Toaster />
      <style>{`
        .jc-drag-row {
          cursor: grab;
        }
        .jc-drag-row:active {
          cursor: grabbing;
        }
        .jc-drag-row.dragging {
          opacity: 0.55;
          background: #e8f0fe !important;
        }
        .jc-drag-handle {
          color: #94a3b8;
          cursor: grab;
          padding: 0 4px;
        }
        .jc-move-btns {
          display: inline-flex;
          flex-direction: column;
          gap: 2px;
          margin-left: 6px;
        }
        .jc-move-btns button {
          border: 1px solid #cbd5e1;
          background: #fff;
          line-height: 1;
          padding: 1px 5px;
          font-size: 10px;
          border-radius: 3px;
          color: #475569;
        }
        .jc-move-btns button:hover {
          background: #f1f5f9;
        }
      `}</style>
      <div className="container mt-1">
        <div className="row mb-2 align-items-center">
          <div className="col">
            <h1 className="mb-1">JC Time Availablity</h1>
            <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
              Drag rows (or use ↑ ↓) to reorder. Order is saved automatically.
              {isSavingOrder ? " Saving..." : ""}
            </p>
          </div>
          <div className="col-auto">
            <Link className="btn btn-sm btn-outline-primary" to="/jobcart/jc_time_report">
              Workload Report
            </Link>
          </div>
        </div>
        <div className="table-responsive">
          {isLoading ? (
            <TaskSkeleton />
          ) : (
            <table className="table table-striped">
              <thead className="table-primary">
                <tr>
                  <th style={{ width: "70px" }}></th>
                  <th className="text-danger">No</th>
                  <th>Name</th>
                  <th>Total Time</th>
                  <th>Total Pending</th>
                  <th>Today</th>
                  <th>Tomorrow</th>
                  <th>Day-after</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((taskuser, index) => (
                  <tr
                    key={taskuser.id}
                    className={`jc-drag-row${dragIndex === index ? " dragging" : ""}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                  >
                    <td>
                      <span className="jc-drag-handle" title="Drag to reorder">
                        <FontAwesomeIcon icon={faGripVertical} />
                      </span>
                      <span className="jc-move-btns">
                        <button
                          type="button"
                          onClick={() => moveRow(index, -1)}
                          disabled={index === 0 || isSavingOrder}
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveRow(index, 1)}
                          disabled={index === tasks.length - 1 || isSavingOrder}
                          title="Move down"
                        >
                          ↓
                        </button>
                      </span>
                    </td>
                    <td>{index + 1}</td>
                    <td className="text-uppercase">
                      {taskuser.name} {taskuser.last_name}
                    </td>
                    <td>
                      <b>{taskuser.total_est_time}</b> Hours
                    </td>
                    <td width="17%">
                      {renderProgressBar(
                        taskuser.total_pending_percent,
                        taskuser.total_pending_leave
                      )}
                      <div className="row">
                        <span className="text-center col-12">
                          <b>
                            {taskuser.total_pending_leave
                              ? "leave"
                              : `${taskuser.total_pending} Tasks`}
                          </b>
                        </span>
                      </div>
                    </td>
                    <td width="17%">
                      {renderProgressBar(
                        taskuser.today_percentage,
                        taskuser.today_leave
                      )}
                      <div className="row">
                        <span className="text-center col-12">
                          <b>
                            {taskuser.today_leave
                              ? "leave"
                              : `${taskuser.today_est_time} Hr`}
                          </b>
                        </span>
                      </div>
                    </td>
                    <td width="17%">
                      {renderProgressBar(
                        taskuser.tommorrow_percentage,
                        taskuser.tommorrow_leave
                      )}
                      <div className="row">
                        <span className="text-center col-12">
                          <b>
                            {taskuser.tommorrow_leave
                              ? "leave"
                              : `${taskuser.tommorrow_est_time} Hr`}
                          </b>
                        </span>
                      </div>
                    </td>
                    <td width="17%">
                      {renderProgressBar(
                        taskuser.dayafter_percentage,
                        taskuser.dayafter_leave
                      )}
                      <div className="row">
                        <span className="text-center col-12">
                          <b>
                            {taskuser.dayafter_leave
                              ? "leave"
                              : `${taskuser.dayafter_est_time} Hr`}
                          </b>
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
